
import { Component, Prop, Vue } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

import { defaultLedgerType } from '@/helpers/Environment'
import { randomIdentifier } from '@/helpers/StringHelpers'
import { reportFormValidity } from '@/helpers/Validation'

import FormInput2 from '@/components/forms/FormInput2.vue'

import { API } from '@/api/API'
import ValidationError from '@/errors/ValidationError'

@Component({
  name: 'ApplyGiftCardsModal',
  components: { FormInput2 },
  computed: {
    ...mapGetters({
      appliedCards: 'Cart/giftCards',
      paymentDueByCreditCard: 'Cart/paymentDueByCreditCard',
    }),
  },
})
export default class extends Vue {
  @Prop({ default: false })
  processing: boolean

  newCardNumber: string = ''
  applying: boolean = false
  error: string = ''

  readonly errorId = 'gift-card-error-' + randomIdentifier()

  appliedCards

  async apply(): Promise<void> {
    // Clear the error before each submit so screenreaders read it aloud every time
    this.error = ''

    try {
      await reportFormValidity(this.$refs.form as HTMLFormElement)
    } catch (error) {
      if (error instanceof ValidationError) return

      throw error
    }

    if (this.appliedCards.some((card) => card.number === this.newCardNumber)) {
      // Let the cleared error flush so the v-if alert re-inserts on re-set.
      await this.$nextTick()
      this.error = 'Oops! You already applied this gift card'
      this.focusInputOnError()
      return
    }

    this.applying = true

    const input = this.$refs.cardNumber as HTMLInputElement
    const cardNumber = encodeURIComponent(input.value)
    API.getUncached(`ledger/${defaultLedgerType}/${cardNumber}`)
      .then((result) => {
        const giftCard = result.ledger._data[0]
        const { number, state } = giftCard
        const balance = parseFloat(giftCard.balance)

        if (balance <= 0 || giftCard.state !== 'active') {
          this.error = 'Gift Card balance is $0'
        } else {
          // Store the new gift card number.
          this.$store.commit('Cart/addGiftCard', { number, balance, state })

          // Reset state.
          this.newCardNumber = ''
          this.error = ''
          this.$modals.close()
        }
      })
      .catch((e) => {
        if (e.response && e.response.status === 404) {
          this.error = this.t.giftCardNotFound as string
        } else {
          this.error = 'We are having trouble with that sorry. Please try again later.'
          throw new Error('Unexpected error applying gift card number')
        }
      })
      .finally(() => {
        this.applying = false
        this.focusInputOnError()
      })
  }

  // Focus the input on error so screen readers announce it via aria-describedby.
  // Deferred so focus lands after the input is re-enabled (`applying` reset).
  focusInputOnError() {
    if (!this.error) return
    this.$nextTick(() => {
      const input = this.$el.querySelector('input')
      if (input instanceof HTMLInputElement) input.focus()
    })
  }
}
